import { Controller } from "@hotwired/stimulus"

/**
 * Key Terms Flip Cards (theme-appraisal "Immersive Editorial")
 *
 * Upgrades the authored Key Terms callout
 * (`.callout-definition[data-title="Key Terms"]` full of `.key-term` pills)
 * into the handoff's flip-card grid. The box is found by selector, so it works
 * wherever it sits in the chapter — opener (ch 1) or end-of-chapter study aid
 * before the review exercises (ch 3); placement is a per-chapter choice. QA
 * decisions (Jess):
 *  - fronts stay a fixed 96px so the grid never reflows; the flipped back
 *    grows downward as a popover floating over the row below
 *  - ONE card flipped at a time — flipping a card animates the previous one
 *    shut (removes the overlapping-popovers problem entirely)
 *  - grids cap at 11 cards + a "View all N terms" tile; the tile toggles the
 *    rest in place ("Show fewer"). Chapters with <= 12 terms show everything.
 *
 * CONTENT RULE (Jess, rev. 2026-07-15): a card only flips if its pill carries a
 * data-definition. Definitions are verbatim from the book where it defines the
 * term, otherwise a short supplied definition (derived-from-chapter / general
 * knowledge) logged in appraisal-author-qa.txt — see the csepub-appraisal-convert
 * skill. A pill with no data-definition still renders as a static card (no back
 * face); the reading-flow body text is never authored.
 *
 * Without JS the authored pill box simply renders as-is (progressive enhancement).
 *
 * Reading focus: when chapter-outline has dimmed unassigned sections
 * (`?sections=…`), a card whose term is first used inside a dimmed section is
 * dimmed too (`.muted`) — a term "belongs" to the section that introduces it,
 * which is where the body's `.key-term` highlight sits. Cards are never removed.
 *
 * OpenStax: the source is instead the chapter's Key Terms glossary
 * (`.os-glossary-list`, one <dl><dt>term<dd>definition per term — every term
 * defined, verbatim, nothing authored). The OpenStax reader passes cap=0: a
 * page that IS the glossary shows every card.
 *
 * Usage: data-controller="key-terms" on the theme wrapper element.
 */
const VISIBLE_CAP = 11

export default class extends Controller {
  static values = {
    // resting-grid cap before the "View all" tile; 0 = show everything
    cap: { type: Number, default: VISIBLE_CAP }
  }

  connect() {
    const source = this.#source()
    if (!source) return
    const { box, entries } = source
    this.flippedCard = null

    const section = document.createElement("div")
    section.className = "key-terms-section"

    const header = document.createElement("div")
    header.className = "key-terms-header"
    const label = document.createElement("span")
    label.className = "key-terms-label"
    label.textContent = source.label || "Key Terms"
    header.appendChild(label)
    if (source.count) {
      const count = document.createElement("span")
      count.className = "key-terms-count"
      count.textContent = `${entries.length} term${entries.length === 1 ? "" : "s"}`
      header.appendChild(count)
    }
    if (entries.some((e) => e.definition)) {
      const hint = document.createElement("span")
      hint.className = "key-terms-hint"
      hint.textContent = "Tap a card to flip →"
      header.appendChild(hint)
    }
    section.appendChild(header)

    const grid = document.createElement("div")
    grid.className = "key-terms-grid"
    const bodyTerms = this.#bodyTerms(box)
    const cards = entries.map(({ term, definition }) => {
      const card = definition ? this.#flipCard(term, definition) : this.#staticCard(term)
      if (this.#introducedInMutedSection(term, bodyTerms)) card.classList.add("muted")
      return card
    })
    cards.forEach((card) => grid.appendChild(card))

    // cap the resting grid at 11 cards + the view-all tile (a 12th term would
    // fit in the tile's slot anyway, so only cap when there are 13+)
    const cap = this.capValue
    if (cap > 0 && cards.length > cap + 1) {
      const extras = cards.slice(cap)
      extras.forEach((card) => card.classList.add("collapsed"))
      grid.appendChild(this.#viewAllTile(extras, cards.length))
    }

    section.appendChild(grid)
    if (source.keep) {
      // the glossary list stays in the DOM (hidden) as the print / no-JS view
      box.insertAdjacentElement("beforebegin", section)
      box.hidden = true
    } else {
      box.replaceWith(section)
    }
  }

  // Authored Key Terms box (our themes) or the OpenStax glossary list.
  #source() {
    const box = this.element.querySelector('.callout-definition[data-title="Key Terms"]')
    if (box) {
      const pills = Array.from(box.querySelectorAll(".key-term"))
      if (!pills.length) return null
      return { box, entries: pills.map((p) => ({ term: p.textContent.trim(), definition: p.dataset.definition })) }
    }
    const glossary = this.element.querySelector(".os-glossary-list")
    if (glossary) {
      const entries = Array.from(glossary.querySelectorAll("dl"))
        .map((dl) => ({
          term: dl.querySelector("dt")?.textContent.trim(),
          definition: dl.querySelector("dd")?.textContent.trim().replace(/\s+/g, " ")
        }))
        .filter((e) => e.term)
      if (!entries.length) return null
      return { box: glossary, entries, keep: true, label: "Flip cards", count: true }
    }
    return null
  }

  // Body highlights (outside the deck), only when reading focus is active.
  #bodyTerms(box) {
    if (!this.element.classList.contains("has-reading-focus")) return []
    return Array.from(this.element.querySelectorAll('.key-term, [data-type="term"]')).filter((el) => !box.contains(el))
  }

  // The body highlight may be inflected ("client pressures" for "client
  // pressure"), so match on a shared prefix after normalizing.
  #introducedInMutedSection(term, bodyTerms) {
    if (!bodyTerms.length) return false
    const norm = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim()
    const wanted = norm(term)
    const first = bodyTerms.find((el) => {
      const found = norm(el.textContent)
      return found === wanted || found.startsWith(wanted) || wanted.startsWith(found)
    })
    return Boolean(first?.closest(".focus-muted"))
  }

  #viewAllTile(extras, total) {
    const tile = document.createElement("button")
    tile.type = "button"
    tile.className = "key-terms-toggle"
    tile.setAttribute("aria-expanded", "false")
    const showAll = `View all ${total} terms`
    tile.textContent = showAll
    tile.addEventListener("click", () => {
      const expanded = tile.getAttribute("aria-expanded") === "true"
      extras.forEach((card) => card.classList.toggle("collapsed", expanded))
      tile.setAttribute("aria-expanded", String(!expanded))
      tile.textContent = expanded ? showAll : "Show fewer"
      // collapsing while an extra card is flipped would strand its popover
      if (expanded && this.flippedCard?.classList.contains("collapsed")) {
        this.#unflip(this.flippedCard)
      }
    })
    return tile
  }

  #unflip(card) {
    card.classList.remove("flipped")
    card.setAttribute("aria-pressed", "false")
    if (this.flippedCard === card) this.flippedCard = null
  }

  #flipCard(term, definition) {
    const card = document.createElement("button")
    card.type = "button"
    card.className = "key-term-card"
    card.setAttribute("aria-pressed", "false")

    const inner = document.createElement("div")
    inner.className = "key-term-card-inner"

    const front = document.createElement("div")
    front.className = "key-term-card-front"
    const name = document.createElement("span")
    name.className = "key-term-card-term"
    name.textContent = term
    const hint = document.createElement("span")
    hint.className = "key-term-card-hint"
    hint.textContent = "Tap to define"
    front.appendChild(name)
    front.appendChild(hint)

    const back = document.createElement("div")
    back.className = "key-term-card-back"
    back.dataset.term = term
    const backText = document.createElement("span")
    backText.className = "key-term-card-back-text"
    backText.textContent = definition
    back.appendChild(backText)

    inner.appendChild(front)
    inner.appendChild(back)
    card.appendChild(inner)

    card.addEventListener("click", () => {
      // accordion: only one card open at a time — the previous one flips shut
      if (this.flippedCard && this.flippedCard !== card) this.#unflip(this.flippedCard)
      const flipped = card.classList.toggle("flipped")
      card.setAttribute("aria-pressed", String(flipped))
      this.flippedCard = flipped ? card : null
    })
    return card
  }

  #staticCard(term) {
    const card = document.createElement("div")
    card.className = "key-term-card static"
    const front = document.createElement("div")
    front.className = "key-term-card-front"
    const name = document.createElement("span")
    name.className = "key-term-card-term"
    name.textContent = term
    front.appendChild(name)
    card.appendChild(front)
    return card
  }
}
